import Decimal from 'decimal.js'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import {
    PaymentContract,
    OrderPaymentStatus,
    GiftCardDetailContract,
    PaymentSettingContract,
    TipsType,
    PaymentStatus,
    PaymentResultContract,
    SalePaymentInfoContract,
    ReceivePaymentContract,
} from 'models'
import { useMemo, useCallback, useState } from 'react'
import paymentService from 'Services/api/payment.service'
import paymentAccountsService from 'Services/api/paymentAccounts.service'
import {
    PAYMENT_TYPE_ID,
    TRANSACTION_PROCESSED_ERROR,
} from 'constant/paymentType'
import { useAppSelector } from 'store/useStoreHook'
import toast from 'utilities/toast/toast'
import {
    CalculatorSwitchMode,
    IPaymentTypeInfo,
} from '../../CalculationInput/CalculatePanel'
import { PaymentDisplaySection } from './SectionType'
import { IPaymentTypeSelection, emptySale } from './usePaymentContext'
import { usePaymentCalculation } from './usePaymentCalculation'
import { round } from 'lodash-es'
import { PaymentTransactionStatus } from 'models/PaymentTransactionStatus'
import { useTranslation } from 'react-i18next'
import { PaymentResult } from 'components/Sell/payment/Models/PaymentResult'
import { PaymentIntegrationProcessStatus } from '../Models/Enum'
import { toSnakeCase } from 'utilities/stringHelper'
import {
    PAYMENT_INTEGRATION_COMPLETE,
    PAYMENT_INTEGRATION_PROCESSING,
} from 'utilities/Payments/PaymentIntegrationStatus'
import { PaymentFormModel } from '../dialogs/splitPaymentDialog/SplitPaymentContext'
import { useSalePaymentEventSubscription } from 'hooks/usePaymentEventSubscription/useSalePaymentEventSubscription'
import { logError } from 'utilities/logError'
import errorMessages from 'utilities/errorMessages'
import { CustomerAccountPaymentModel } from '../dialogs/CustomerAccountsSellPopup/CustomerAccountsPaymentMobile'
import { IPaymentDialogProps } from '../PaymentDialog'
import useCurrentTerminalDevicesHwAgentCaller from 'hooks/useHardwareAgentCaller/useCurrentTerminalDevicesHwAgentCaller'
export type PaymentPayload = ReceivePaymentContract & {
    /**account or gift card name */
    accountName?: string
    /** account or gift card balance */
    accountBalance?: number
}
export const validationLimit = (
    paymentAmount: number,
    isOffline: boolean,
    paymentSetting: PaymentSettingContract,
) => {
    if (
        isOffline &&
        /** To disabled some payment types in offline mode */
        [
            PAYMENT_TYPE_ID.CHANNEL_ACCOUNT,
            PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT,
            PAYMENT_TYPE_ID.GIFTCARD,
            PAYMENT_TYPE_ID.EVO_PAYMENT,
        ].includes(paymentSetting.paymentTypeID!)
    )
        return false
    if (
        (paymentSetting.minimumAmountLimit ?? 0) !== 0 &&
        paymentAmount < paymentSetting.minimumAmountLimit!
    )
        return false
    if (
        (paymentSetting.maximumAmountLimit ?? 0) !== 0 &&
        paymentAmount > paymentSetting.maximumAmountLimit!
    )
        return false
    return true
}
interface IProps
    extends Pick<
        IPaymentDialogProps,
        | 'isOffline'
        | 'onFinishSalePayment'
        | 'onFinishSinglePayment'
        | 'onProcessingSale'
        | 'finalizeOrder'
        | 'onCloseReceiptDialog'
        | 'onPaymentError'
        | 'onCloseDialog'
        | 'useTip'
        | 'getSaleDetailByID'
        | 'orderCustomerID'
        | 'tipsSetting'
        | 'paymentSettings'
        | 'totalDueAmount'
    > {
    orderID: string | undefined
    isNegativeTotalDue: boolean
}
const cEmptySale = emptySale()

export const usePaymentDialog = ({
    paymentSettings,
    tipsSetting,
    useTip,

    orderCustomerID,
    orderID,

    finalizeOrder,
    onCloseReceiptDialog,
    onFinishSalePayment,
    onPaymentError,
    onCloseDialog,

    onProcessingSale,
    getSaleDetailByID,
    onFinishSinglePayment,

    isNegativeTotalDue,
    isOffline = false,
    totalDueAmount: orderTotalDueAmount,
}: IProps) => {
    const { t } = useTranslation()
    const { openDrawer } = useCurrentTerminalDevicesHwAgentCaller()

    const [
        ,
        checkIntegrationProcess,
        checkIntegrationProcessStatus,
    ] = useFetchApi(paymentService.checkPaymentIntegrationStatus)
    const [accounts, fetchAccounts, fetchAccountStatus] = useFetchApi(
        paymentAccountsService.getCustomerPaymentAccounts,
    )
    const { currencyID = 0 } = useAppSelector((state) => state.operation.data)
    const [showRedeemPointForm, setShowRedeemPointForm] = useState<boolean>(
        false,
    )
    const [
        paymentProcessErrorMessage,
        setPaymentProcessErrorMessage,
    ] = useState<string>('')
    const [
        paymentProcessAdditionalInstruction,
        setPaymentProcessAdditionalInstruction,
    ] = useState<string>('')
    const [sale, setSale] = useState<PaymentResult>(cEmptySale)
    const [paidPayments, setPaidPayments] = useState<PaymentContract[]>([])
    const [selectedGiftCard, onSelectGiftCard] = useState<
        GiftCardDetailContract | undefined
    >()

    const [isSplitPayment, setIsSplitPayment] = useState(false)
    const [splitPaymentForms, setSplitPaymentForms] = useState<
        PaymentFormModel[]
    >([])

    const [tenderedAmount, setTenderedAmount] = useState(0)
    const [selectedPaymentType, setSelectedPaymentType] = useState<
        PaymentSettingContract | undefined
    >()
    const [paymentAccountCustomerID, setPaymentAccountCustomerID] = useState<
        string | undefined
    >()
    const [expectAmount, setExpectAmount] = useState<Decimal>(new Decimal(0))
    const [selectedCustomerAccount, setSelectedCustomerAccount] = useState<
        CustomerAccountPaymentModel | undefined
    >(undefined)

    const [openReceipt, setOpenReceipt] = useState(false)
    const [isSubmiting, setIsSubmiting] = useState(false)

    const [addTipsMode, setAddTipsMode] = useState<boolean>(false)
    const [isOnQuickPayment, setIsQuickPayment] = useState<boolean>(false)

    const [tipsAmount, setTipsAmount] = useState<Decimal>(new Decimal(0))
    const [tipsRate, setTipsRate] = useState<Decimal>(new Decimal(0))
    const [tipsType, setTipsType] = useState<CalculatorSwitchMode>(
        tipsSetting?.defaultTipsType === TipsType.FixedAmount ||
            // Negative Total Due cannot be calculated by percentage
            isNegativeTotalDue
            ? CalculatorSwitchMode.Custom
            : CalculatorSwitchMode.Percentage,
    )

    const [pointRedeem, setPointRedeem] = useState<number>()
    const [redeemDiscount, setRedeemDiscount] = useState<number>()

    const [receivedCash, setReceivedCash] = useState<number>(0)

    const [
        paymentIntegrationProcessStatus,
        setPaymentIntegrationProcessStatus,
    ] = useState<PaymentIntegrationProcessStatus>(
        PaymentIntegrationProcessStatus.IDLE,
    )

    const [
        integrationPaymentTransactionId,
        setIntegrationTransactionId,
    ] = useState<string>('')
    const [currentSection, onSetDisplaySection] = useState(
        PaymentDisplaySection.Calculation,
    )

    const paymentDetail: IPaymentTypeInfo = useMemo(() => {
        return {
            paymentType: selectedPaymentType?.paymentTypeID,
            isCutomPayment: !!selectedPaymentType?.customPaymentTypeID,
            paymentAccount: selectedCustomerAccount,
        }
    }, [selectedCustomerAccount, selectedPaymentType])

    const defaultAccountFromCustomer = useMemo(
        () =>
            (accounts ?? []).find(
                (a) =>
                    a.customerPaymentAccounts?.some(
                        (b) => b.customerID === (orderCustomerID ?? ''),
                    ) && a.isActive,
            ),
        [accounts, orderCustomerID],
    )
    const payment: PaymentPayload = useMemo(() => {
        const paymentWithName: PaymentPayload = {
            tenderedAmount: tenderedAmount,
            paymentTypeID: selectedPaymentType?.paymentTypeID!,
            customPaymentTypeID: selectedPaymentType?.customPaymentTypeID,
            paymentAccountID: selectedCustomerAccount?.accountID,
            giftCardID: selectedGiftCard?.giftCardID!,
        }
        if (
            selectedPaymentType?.paymentTypeID ===
            PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT
        ) {
            paymentWithName.accountName = selectedCustomerAccount?.name!
            paymentWithName.accountBalance =
                selectedCustomerAccount?.accountBalance ?? 0
        }
        if (selectedPaymentType?.paymentTypeID === PAYMENT_TYPE_ID.GIFTCARD) {
            paymentWithName.accountBalance =
                selectedGiftCard?.currentBalance ?? 0
            paymentWithName.giftCardID = selectedGiftCard?.giftCardID!
        }
        return paymentWithName
    }, [
        selectedCustomerAccount,
        selectedGiftCard?.currentBalance,
        selectedGiftCard?.giftCardID,
        selectedPaymentType?.customPaymentTypeID,
        selectedPaymentType?.paymentTypeID,
        tenderedAmount,
    ])

    const {
        cashRoundingAdjustAmount,
        changeValue,
        balanceDueAmount,
        balanceDueAmountCalculation,
    } = usePaymentCalculation({
        selectedPaymentType,
        payment,
        expectAmount,
    })
    const paymentTypeSelections = useMemo(
        () =>
            paymentSettings
                .filter((a) => a.isAvailableInPOS)
                .map<IPaymentTypeSelection>((p) => {
                    return {
                        name: p.name!,
                        paymentSettingID: p.paymentSettingID!,
                        paymentTypeID: p.paymentTypeID!,
                        customPaymentTypeID: p.customPaymentTypeID!,
                        customLogoUrl: p.customLogoUrl!,
                        isCashRoundingEnabled: p.isCashRoundingEnabled,
                        roundOffTotalsToNearest: p.roundOffTotalsToNearest,
                        disabled: (expectAmount: number) =>
                            !validationLimit(expectAmount, !!isOffline, p),
                    }
                }),
        [isOffline, paymentSettings],
    )
    const isAddTipsEnabled = useMemo(() => useTip ?? false, [useTip])

    const defaultPaymentType = useMemo(
        () =>
            paymentTypeSelections.filter(
                (a) => !a.disabled(expectAmount.toNumber()),
            )?.[0],
        [expectAmount, paymentTypeSelections],
    )

    const quickTips = useMemo(
        () => tipsSetting?.quickTips?.filter((i) => (i.amount || 0) > 0) ?? [],
        [tipsSetting],
    )

    const noQuickTip = useMemo(
        () => !(tipsSetting?.isQuickTip ?? false) || quickTips.length === 0,
        [tipsSetting?.isQuickTip, quickTips],
    )

    const quickButtons = useMemo(() => {
        const buttons: number[] = []
        buttons.push(balanceDueAmount.toNumber())
        if (addTipsMode) {
            return quickTips
                .filter(
                    (i) =>
                        (i.type === TipsType.Percentage
                            ? CalculatorSwitchMode.Percentage
                            : CalculatorSwitchMode.Custom) === tipsType,
                )
                .flatMap((i) => i.amount ?? 0)
        } else if (
            selectedPaymentType?.paymentTypeID === PAYMENT_TYPE_ID.CASH &&
            selectedPaymentType?.isQuickCashPaymentEnabled
        ) {
            buttons.push(selectedPaymentType?.quickCashButton1 ?? 0)
            buttons.push(selectedPaymentType?.quickCashButton2 ?? 0)
            buttons.push(selectedPaymentType?.quickCashButton3 ?? 0)
            buttons.push(selectedPaymentType?.quickCashButton4 ?? 0)
            buttons.push(selectedPaymentType?.quickCashButton5 ?? 0)
        }

        return buttons
    }, [
        balanceDueAmount,
        selectedPaymentType,
        quickTips,
        tipsType,
        addTipsMode,
    ])

    const isSaleComplete = useMemo(
        () => sale?.paymentStatus === OrderPaymentStatus.Paid,
        [sale?.paymentStatus],
    )

    const customerAccounts = useMemo(() => accounts ?? [], [accounts])

    const onSelectPaymentType = useCallback(
        (paymentSettingID: string) => {
            const selectedPayment = paymentSettings.find(
                (a) => a.paymentSettingID === paymentSettingID,
            )!
            const paymentTypeID = selectedPayment.paymentTypeID!
            setSelectedPaymentType(selectedPayment)
            if (paymentTypeID === PAYMENT_TYPE_ID.CASH) {
                setTenderedAmount(0)
                setReceivedCash(0)
            } else {
                setTenderedAmount(expectAmount.toNumber())
            }

            if (paymentTypeID === PAYMENT_TYPE_ID.CUSTOMER_ACCOUNT) {
                if (!selectedCustomerAccount) {
                    if (!defaultAccountFromCustomer) {
                        // setOpenCustomerAccount(true)
                        onSetDisplaySection(PaymentDisplaySection.AccountList)
                    } else
                        setSelectedCustomerAccount(defaultAccountFromCustomer)
                }
                return
            }

            // setOpenCustomerAccount(false)
            setSelectedCustomerAccount(undefined)
            onSetDisplaySection(PaymentDisplaySection.Calculation)

            if (paymentTypeID === PAYMENT_TYPE_ID.GIFTCARD) {
                //set ui stage
                onSetDisplaySection(PaymentDisplaySection.GiftCard)
                return
            }

            onSelectGiftCard(undefined)

            if (paymentTypeID === PAYMENT_TYPE_ID.CREDITCARD) {
                onSetDisplaySection(PaymentDisplaySection.CreditCard)
                return
            } else onSetDisplaySection(PaymentDisplaySection.Calculation)
        },
        [
            paymentSettings,
            expectAmount,
            selectedCustomerAccount,
            defaultAccountFromCustomer,
        ],
    )

    const resetPaymentDetail = useCallback(() => {
        setSale(cEmptySale)
        setExpectAmount(new Decimal(0))
        setTenderedAmount(0)
        setReceivedCash(0)
        setPaymentAccountCustomerID(undefined)
        onSelectGiftCard(undefined)
        setIsSplitPayment(false)
        setRedeemDiscount(0)
        setPointRedeem(0)
        setSplitPaymentForms([])
        setPaidPayments([])
    }, [])

    const resetDialog = useCallback(() => {
        onSelectGiftCard(undefined)
        onSetDisplaySection(PaymentDisplaySection.Calculation)
        setSelectedCustomerAccount(undefined)
        setSelectedPaymentType(defaultPaymentType)
        setIsSplitPayment(paidPayments.length > 0)
        setShowRedeemPointForm(false)
        resetPaymentDetail()
    }, [defaultPaymentType, paidPayments.length, resetPaymentDetail])

    const onCloseReceipt = useCallback(
        (isLogout: boolean) => {
            setOpenReceipt(false)
            if (!isSaleComplete) {
                return
            }
            onCloseReceiptDialog?.(isLogout)
            resetDialog()
            onCloseDialog()
        },
        [onCloseReceiptDialog, isSaleComplete, resetDialog, onCloseDialog],
    )

    const onSetExpectAmount = useCallback(
        (amount: Decimal, newPaymentType?: number) => {
            let newAmount = amount
            if (tipsAmount.greaterThan(new Decimal(0)))
                newAmount = newAmount.plus(tipsAmount)
            setExpectAmount(newAmount)
            const pType = newPaymentType ?? selectedPaymentType?.paymentTypeID
            if (pType && pType !== PAYMENT_TYPE_ID.CASH) {
                setTenderedAmount(newAmount.toNumber())
            }
        },
        [selectedPaymentType, tipsAmount],
    )
    const onSelectCustomerAccount = useCallback(
        (customerAccount: CustomerAccountPaymentModel) => {
            setSelectedCustomerAccount(customerAccount)
            onSetDisplaySection(PaymentDisplaySection.Calculation)
        },
        [],
    )
    const onCloseDialogWithReset = useCallback(() => {
        onCloseDialog()
        resetDialog()
    }, [onCloseDialog, resetDialog])

    const onPaymentSuccess = useCallback(
        async (newSale: PaymentResult) => {
            setOpenReceipt(true)
            onFinishSinglePayment?.(newSale)
            if (newSale?.paymentStatus === OrderPaymentStatus.Paid) {
                onFinishSalePayment?.(newSale, isOffline)
                await finalizeOrder?.()
            }
        },
        [finalizeOrder, isOffline, onFinishSalePayment, onFinishSinglePayment],
    )

    const onCatchPaymentError = useCallback(
        (error: any) => {
            onPaymentError?.(error)
            const { message, arg } = errorMessages(error)
            const errorMessage = t(message, {
                ...arg,
                interpolation: {
                    escapeValue: false,
                },
            })
            setPaymentProcessErrorMessage(errorMessage)
            if (message === 'payment.error.payment_device_required') {
                setPaymentProcessAdditionalInstruction(
                    'payment_device_required',
                )
            }
            if (message === 'payment_device.error.invalid_config') {
                setPaymentProcessAdditionalInstruction('invalid_config')
            }
            logError('usePaymentDialog', 'process sale', error)
            setPaymentIntegrationProcessStatus(
                PaymentIntegrationProcessStatus.FAIL,
            )
        },
        [onPaymentError, t],
    )

    const onSubmitPayment = useCallback(async () => {
        try {
            setIsSubmiting(true)
            if (!orderID)
                throw Error('payment process is waiting for order processing')

            setPaymentIntegrationProcessStatus(
                PaymentIntegrationProcessStatus.LOADING,
            )
            setIntegrationTransactionId('')
            setPaymentIntegrationPaymentID(undefined)
            payment.changeAmount = changeValue.toNumber()
            payment.tipAmount = tipsAmount.toNumber()
            payment.expectAmount = expectAmount.toNumber()
            payment.cashRoundingAdjustAmount = cashRoundingAdjustAmount.toNumber()
            payment.tenderedAmount = tenderedAmount
            const resultSale: PaymentResult = await onProcessingSale(
                payment,
                orderCustomerID!,
                currencyID,
                isOffline,
                pointRedeem ?? 0,
                orderID,
            )
            if (payment.paymentTypeID === PAYMENT_TYPE_ID.CASH) {
                await openDrawer('usePaymentDialog')
            }

            setSale(resultSale)
            const { payments: salePayments = [] } = resultSale
            const hasUnFinishPayment = !!salePayments?.some(
                (f) => f.status === PaymentStatus.AuthorisedPending,
            )
            //if the payment is integration. it will display the waiting dialog
            if (hasUnFinishPayment) {
                const p = (salePayments ?? []).find(
                    (f) =>
                        f.paymentTypeID === PAYMENT_TYPE_ID.EVO_PAYMENT &&
                        f.transactionID !== null,
                )
                setIntegrationTransactionId(p?.transactionID ?? '')
            } else {
                await onPaymentSuccess(resultSale)
                setPaymentIntegrationProcessStatus(
                    PaymentIntegrationProcessStatus.SUCCESS,
                )
                const newPayments = resultSale.payments?.find(
                    (p: SalePaymentInfoContract | PaymentContract) =>
                        !paidPayments.some((r) => r.paymentID === p.paymentID),
                )

                setPaidPayments(
                    resultSale.payments?.filter(
                        (a) => a.status === PaymentStatus.Received,
                    ) ?? [],
                )
                return newPayments?.paymentID
            }
        } catch (err: any) {
            onCatchPaymentError(err)
            return undefined
        } finally {
            setIsSubmiting(false)
        }
    }, [
        orderID,
        payment,
        changeValue,
        tipsAmount,
        expectAmount,
        cashRoundingAdjustAmount,
        tenderedAmount,
        onProcessingSale,
        orderCustomerID,
        currencyID,
        isOffline,
        pointRedeem,
        openDrawer,
        onPaymentSuccess,
        paidPayments,
        onCatchPaymentError,
    ])

    const processResponseFromIntregrationPayment = useCallback(
        async (status: PaymentTransactionStatus, isFromCallback: boolean) => {
            const messageDescription = `${t(
                `quick_sell.evo_payment.notification.${toSnakeCase(status)}`,
            )}`
            if (PAYMENT_INTEGRATION_COMPLETE.includes(status)) {
                const newSale:
                    | PaymentResult
                    | undefined = await getSaleDetailByID(sale?.saleID ?? '')

                setSale({ ...newSale })
                await onPaymentSuccess(newSale!)
                const newPayments = newSale?.payments?.find(
                    (p: SalePaymentInfoContract | PaymentContract) =>
                        !paidPayments.some((r) => r.paymentID === p.paymentID),
                )

                setPaidPayments((a) => [...a, newPayments!])
                setIsSubmiting(false)
                if (!isFromCallback) toast.success(messageDescription)
                return newPayments?.paymentID
            } else {
                if (PAYMENT_INTEGRATION_PROCESSING.includes(status)) {
                    toast.info(messageDescription)
                    setPaymentIntegrationProcessStatus(
                        PaymentIntegrationProcessStatus.LOADING,
                    )
                } else {
                    toast.error(messageDescription)
                    setPaymentProcessErrorMessage(messageDescription)
                    setPaymentIntegrationProcessStatus(
                        PaymentIntegrationProcessStatus.FAIL,
                    )
                    onPaymentError?.('')
                }
                return
            }
        },
        [
            getSaleDetailByID,
            sale?.saleID,
            onPaymentSuccess,
            t,
            paidPayments,
            onPaymentError,
        ],
    )

    //  Use for split payment
    const [
        paymentIntegrationPaymentID,
        setPaymentIntegrationPaymentID,
    ] = useState<string | undefined>(undefined)

    const onReceivePaymentIntegrationResult = useCallback(
        async (res: PaymentResultContract) => {
            if (res?.saleID !== sale?.saleID) {
                return
            }
            const paymentID = await processResponseFromIntregrationPayment(
                res.status!,
                true,
            )
            setPaymentIntegrationPaymentID(paymentID)

            if (paymentID) {
                setPaymentIntegrationProcessStatus(
                    PaymentIntegrationProcessStatus.SUCCESS,
                )
            }
        },
        [sale, processResponseFromIntregrationPayment],
    )
    useSalePaymentEventSubscription(onReceivePaymentIntegrationResult)
    const checkIntegrationPaymentStatus = useCallback(
        async (transactionId: string) => {
            try {
                const result = await checkIntegrationProcess(transactionId)
                const status = result?.status!
                const paymentID = await processResponseFromIntregrationPayment(
                    status,
                    false,
                )
                setPaymentIntegrationPaymentID(paymentID)
                if (paymentID) {
                    setPaymentIntegrationProcessStatus(
                        PaymentIntegrationProcessStatus.SUCCESS,
                    )
                }
            } catch (err: any) {
                setPaymentIntegrationProcessStatus(
                    PaymentIntegrationProcessStatus.FAIL,
                )
                if (err.message !== TRANSACTION_PROCESSED_ERROR) {
                    toast.error(err.message)
                }
            }
        },
        [processResponseFromIntregrationPayment, checkIntegrationProcess],
    )

    const resetEvoPaymentDialogState = useCallback(() => {
        setIsSubmiting(false)
        setIntegrationTransactionId('')
        setPaymentIntegrationProcessStatus(PaymentIntegrationProcessStatus.IDLE)
    }, [])

    const onCloseAccountDialog = useCallback(() => {
        onSetDisplaySection(PaymentDisplaySection.Calculation)
    }, [])

    const onSetTipsAmount = useCallback(
        (amount: Decimal, mode: CalculatorSwitchMode) => {
            if (!(!amount.equals(tipsAmount) || mode !== tipsType)) {
                return
            }
            setTipsType(mode)
            setTipsRate(amount)

            const resetTotalDue = expectAmount.minus(tipsAmount)

            let newTipsAmount = new Decimal(0)
            if (amount.lessThanOrEqualTo(new Decimal(0))) {
                //remove tip amount
                setTipsAmount(new Decimal(0))
            } else {
                newTipsAmount =
                    mode === CalculatorSwitchMode.Percentage
                        ? new Decimal(
                              round(
                                  resetTotalDue
                                      .mul(amount.div(new Decimal(100)))
                                      .toNumber(),
                                  2,
                              ),
                          )
                        : amount
                setTipsAmount(newTipsAmount)
            }
            const newExpectedAmount = resetTotalDue.plus(newTipsAmount)
            setExpectAmount(newExpectedAmount)

            const newBalanceAmount = balanceDueAmountCalculation(
                newExpectedAmount,
            )

            const paymentAmount = new Decimal(receivedCash ?? 0)

            // var isReadyToPay = paymentAmount.greaterThanOrEqualTo(
            //     balanceDueAmount,
            // )

            // if payment is ready to pay before, just add tips (add cash).
            // in case of cash in greater than new expected amount, return tip as change
            // if not, do nothing (add change but not add cash).
            if (
                // isReadyToPay &&
                newBalanceAmount.greaterThan(paymentAmount) &&
                (newTipsAmount.equals(0) ? receivedCash > 0 : true)
            ) {
                // set as balance
                if (newTipsAmount.equals(0)) {
                    setTenderedAmount(receivedCash)
                } else {
                    setTenderedAmount(newBalanceAmount.toNumber())
                }
            } else {
                setTenderedAmount(receivedCash)
            }
        },
        [
            tipsAmount,
            tipsType,
            expectAmount,
            balanceDueAmountCalculation,
            receivedCash,
        ],
    )

    const clearTips = useCallback(() => {
        setExpectAmount((prev) => prev.minus(tipsAmount))
        setTipsRate(new Decimal(0))
        setTipsAmount(new Decimal(0))
        setTenderedAmount(receivedCash)
    }, [tipsAmount, receivedCash])

    const onRedeemSubmit = useCallback(
        (pointToRedeem: number, discount: number, newTotalDue: number) => {
            setPointRedeem(pointToRedeem)
            setRedeemDiscount(discount)
            setExpectAmount(new Decimal(newTotalDue))
            setShowRedeemPointForm(false)
            if (selectedPaymentType?.paymentTypeID === PAYMENT_TYPE_ID.CASH) {
                setTenderedAmount(0)
                setReceivedCash(0)
            } else {
                setTenderedAmount(newTotalDue)
            }
        },
        [selectedPaymentType],
    )
    const onSetSplitPayment = useCallback(
        (value: boolean) => {
            setShowRedeemPointForm(false)
            setRedeemDiscount(0)
            setPointRedeem(0)
            setTipsAmount(new Decimal(0))
            if (!value) {
                //reset tendered amount
                setTenderedAmount(0)
                setReceivedCash(0)
                onSetExpectAmount(new Decimal(orderTotalDueAmount))
            }
            setIsSplitPayment(value)
        },
        [onSetExpectAmount, orderTotalDueAmount],
    )

    const onSetReceivedCash = useCallback(
        (amount: number) => {
            if (addTipsMode) {
                setIsQuickPayment(false)
            } else if (!isOnQuickPayment && !addTipsMode) {
                setIsQuickPayment(true)
            } else if (!addTipsMode) {
                setReceivedCash(amount)
            }
        },
        [addTipsMode, isOnQuickPayment],
    )

    const onQuickSplitGiftCardPayment = useCallback(
        (giftcardDetail: GiftCardDetailContract) => {
            onSetSplitPayment(true)
            onSelectGiftCard(giftcardDetail)
        },
        [onSelectGiftCard, onSetSplitPayment],
    )
    const clearPaymentState = useCallback(() => {
        setPaymentIntegrationProcessStatus(PaymentIntegrationProcessStatus.IDLE)
    }, [setPaymentIntegrationProcessStatus])
    const onEnterAddTipsMode = useCallback(() => {
        setIsQuickPayment(false)
        setAddTipsMode(true)
    }, [])
    const onExitAddTipsMode = useCallback(() => setAddTipsMode(false), [])
    return {
        sale,
        openReceipt,
        openCustomerAccount:
            currentSection === PaymentDisplaySection.AccountList,
        isSubmiting,
        paymentDetail,
        quickButtons,
        isLoadAccount: isLoading(fetchAccountStatus),
        customerAccounts,
        onCloseReceipt,
        onSelectPaymentType,
        onSetExpectAmount,
        onSelectCustomerAccount,
        onSubmitPayment,
        onCloseAccountDialog,
        fetchAccounts,
        setPaidPayments,
        paymentTypeSelections,
        paidPayments,
        selectedCustomerAccount,
        selectedPaymentType,
        payment,
        expectAmount,
        onSetTenderedAmount: setTenderedAmount,
        isSaleComplete,
        changeValue,
        isSplitPayment,
        onSetSplitPayment,
        onCloseDialogWithReset,
        paymentAccountCustomerID,
        onSelectedPaymentCustomerID: setPaymentAccountCustomerID,
        currentSection,
        onSetDisplaySection,
        selectedGiftCard,
        onSelectGiftCard,
        currencyID,
        defaultPaymentType,
        // tips
        addTipsMode,
        onEnterAddTipsMode,
        onExitAddTipsMode,
        onSetTipsAmount,
        tipsAmount,
        tipsType,
        clearTips,
        tipsRate,
        noQuickTip,
        isAddTipsEnabled,

        pointRedeem,
        redeemDiscount,
        onRedeemSubmit,
        showRedeemPointForm,
        setShowRedeemPointForm,
        onSetReceivedCash,

        setPaymentProcessStatus: setPaymentIntegrationProcessStatus,
        paymentProcessStatus: paymentIntegrationProcessStatus,
        evoPaymentTransactionId: integrationPaymentTransactionId,
        checkIntegrationPaymentStatus,
        isCheckingEvoPaymentStatus: isLoading(checkIntegrationProcessStatus),
        resetEvoPaymentDialogState,
        errorMessage: paymentProcessErrorMessage,
        additionalInstruction: paymentProcessAdditionalInstruction,

        paymentIntegrationPaymentID,
        clearPaymentState,
        paymentForms: splitPaymentForms,
        handlePaymentFormsChange: setSplitPaymentForms,
        onQuickSplitGiftCardPayment,
        setSplitPaymentWithOutDeps: setIsSplitPayment,
        tenderedAmount,
    }
}
